import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { SessionStatus } from '@/lib/calendar';

/*
 * Sessions API — list, create, update, delete.
 *
 * The server ([server/src/routes/sessions.js]) accepts a date-range filter
 * on the list endpoint and writes an audit row (`"Sessions listed"`) on
 * every successful list fetch. Single-record mutations produce their own
 * audit rows server-side. Do not re-audit from the client.
 *
 * `day` is intentionally not sent on create or update — the column is
 * redundant with `session_date` and was dropped from the React client
 * write path per the 2026-04-18 stakeholder decision. The server still
 * accepts it for legacy callers.
 */

/**
 * Structured recurrence pattern for a session's series. Replaces the
 * old `recurrence` text label + `seriesEndDate` — no more parsing
 * human-readable strings to figure out cadence.
 *
 *  • weekly — `weekInterval` ≥ 1; `daysOfWeek` is null. The weekday
 *    is implicit in each occurrence's `date`.
 *  • daily  — `daysOfWeek` is non-empty (JS weekday indexes, 0 = Sun
 *    … 6 = Sat); `weekInterval` is null.
 *
 * `startDate` / `endDate` reflect the MIN / MAX of the series's
 * occurrence dates — derived at query time, not stored, so trimming
 * or rescheduling occurrences always reads back accurately.
 */
export interface SessionSeries {
    id: string;
    patternType: 'weekly' | 'daily';
    weekInterval: number | null;
    daysOfWeek: number[] | null;
    startDate: string | null;
    endDate: string | null;
}

export interface SessionRecord {
    id: string;
    clientId: string | null;
    client: string;
    staffId: string | null;
    staff: string;
    /** Denormalized role-type tag on the session: 'bcba' | 'rbt' | 'admin' | ''. */
    type: string;
    /** ISO date string `YYYY-MM-DD`, or null for undated recurring-template rows. */
    date: string | null;
    startH: number;
    startM: number;
    /** Duration in minutes. */
    dur: number;
    label: string;
    status: SessionStatus | string;
    noteStatus: string;
    location: string;
    /** FK to session_types. Named `serviceTypeId` on the wire for legacy reasons. */
    serviceTypeId: string | null;
    /** Shared across every occurrence of a recurring series; null for one-offs. */
    seriesId: string | null;
    /**
     * Structured pattern info for the session's series. null for
     * one-offs and for mutation responses that don't carry the series
     * join (create/patch/delete) — the list refetch that fires after
     * any mutation fills it back in.
     */
    series: SessionSeries | null;
    isSupervised: boolean;
    supervisingBcbaId: string | null;
    /** FK to cancel_reasons; null unless status is a Canceled variant. */
    cancelReasonId: string | null;
}

export interface SessionsRange {
    dateFrom: string;
    dateTo: string;
}

async function fetchSessions(range: SessionsRange): Promise<SessionRecord[]> {
    const res = await api.get<SessionRecord[]>('/sessions', {
        params: { dateFrom: range.dateFrom, dateTo: range.dateTo },
    });
    return res.data;
}

/**
 * Loads the sessions visible in the current calendar view. Pass the
 * view's date window (inclusive on both ends); the server requires it
 * (400 otherwise) so every list audit carries a scoping filter and
 * unbounded reads can't slip in by default.
 */
export function useSessions(range: SessionsRange | undefined) {
    return useQuery({
        queryKey: ['sessions', range?.dateFrom, range?.dateTo] as const,
        queryFn: () => fetchSessions(range!),
        enabled: !!range,
        staleTime: 15_000,
    });
}

export interface SessionCreatePayload {
    clientId?: string | null;
    staffId: string;
    serviceTypeId: string;
    date: string;
    startH: number;
    startM: number;
    dur: number;
    label: string;
    location?: string;
    notes?: string;
    isSupervised?: boolean;
    supervisingBcbaId?: string | null;
    cancelReasonId?: string | null;
    /** Category tag — 'Service' or 'Administrative'; echoed back on status filtering. */
    type?: string;
    status?: SessionStatus;
}

export interface SessionUpdatePayload {
    clientId?: string | null;
    staffId?: string | null;
    serviceTypeId?: string | null;
    date?: string;
    startH?: number;
    startM?: number;
    dur?: number;
    label?: string;
    location?: string;
    notes?: string;
    status?: SessionStatus;
    noteStatus?: string;
    isSupervised?: boolean;
    supervisingBcbaId?: string | null;
    cancelReasonId?: string | null;
}

async function createSession(payload: SessionCreatePayload): Promise<SessionRecord> {
    const res = await api.post<SessionRecord>('/sessions', payload);
    return res.data;
}

async function updateSession(
    id: string,
    payload: SessionUpdatePayload,
): Promise<SessionRecord> {
    const res = await api.patch<SessionRecord>(`/sessions/${id}`, payload);
    return res.data;
}

async function deleteSession(id: string): Promise<void> {
    await api.delete(`/sessions/${id}`);
}

async function completeSession(id: string): Promise<SessionRecord> {
    const res = await api.post<SessionRecord>(`/sessions/${id}/complete`);
    return res.data;
}

export interface SeriesCreatePayload {
    /** Optional — omitted for Administrative session types (trainings, meetings). */
    clientId?: string | null;
    staffId: string;
    serviceTypeId: string;
    startH: number;
    startM: number;
    dur: number;
    label?: string;
    location?: string;
    notes?: string;
    /** Recurrence pattern — exactly one of the two shapes must be provided. */
    patternType: 'weekly' | 'daily';
    /** Required when `patternType === 'weekly'` — steps of N weeks. */
    weekInterval?: number;
    /** Required when `patternType === 'daily'` — JS weekday indexes (0 = Sun … 6 = Sat). */
    daysOfWeek?: readonly number[];
    isSupervised?: boolean;
    supervisingBcbaId?: string | null;
    /** Pre-computed occurrence dates from `generateSeriesDates`. */
    dates: string[];
}

export interface SeriesCreateResponse {
    seriesId: string;
    count: number;
    sessions: SessionRecord[];
}

async function createSeries(payload: SeriesCreatePayload): Promise<SeriesCreateResponse> {
    const res = await api.post<SeriesCreateResponse>('/sessions/series', payload);
    return res.data;
}

/**
 * Fields the bulk-series PATCH accepts. Subset of SessionUpdatePayload —
 * date, clientId, seriesId, cancelReasonId, and noteStatus are
 * intentionally excluded: rewriting dates across a series would scramble
 * occurrence spacing; status→cancel on a whole series needs a dedicated
 * flow; and note status is per-occurrence.
 */
export interface SeriesUpdatePayload {
    staffId?: string | null;
    serviceTypeId?: string | null;
    startH?: number;
    startM?: number;
    dur?: number;
    label?: string;
    location?: string;
    notes?: string;
    status?: SessionStatus;
    isSupervised?: boolean;
    supervisingBcbaId?: string | null;
    cancelReasonId?: string | null;
}

export interface SeriesMutationResponse {
    count: number;
    /** Occurrences whose status is Finalized — server refuses to touch these. */
    skipped: number;
    sessions?: SessionRecord[];
}

async function updateSeries(
    seriesId: string,
    fromDate: string,
    payload: SeriesUpdatePayload,
): Promise<SeriesMutationResponse> {
    const res = await api.patch<SeriesMutationResponse>(
        `/sessions/series/${seriesId}`,
        payload,
        { params: { fromDate } },
    );
    return res.data;
}

async function deleteSeries(
    seriesId: string,
    fromDate: string,
): Promise<SeriesMutationResponse> {
    const res = await api.delete<SeriesMutationResponse>(`/sessions/series/${seriesId}`, {
        params: { fromDate },
    });
    return res.data;
}

export interface SeriesEndDateResponse {
    action: 'extended' | 'shortened' | 'noop';
    count: number;
}

async function updateSeriesEndDate(
    seriesId: string,
    endDate: string,
): Promise<SeriesEndDateResponse> {
    const res = await api.patch<SeriesEndDateResponse>(
        `/sessions/series/${seriesId}/end-date`,
        { endDate },
    );
    return res.data;
}

/**
 * Promote an existing one-off session into the first occurrence of a
 * new recurring series. The existing session row is preserved
 * (attached via recurring_series_id) — any notes, status changes, or
 * audit history on it survive the promotion. The `dates` array must
 * include the session's own date as its first element; every
 * subsequent date becomes a freshly-created Scheduled occurrence.
 *
 * Server rejects when the session is already in a series or is
 * Finalized.
 */
export interface PromoteToSeriesPayload {
    patternType: 'weekly' | 'daily';
    weekInterval?: number;
    daysOfWeek?: readonly number[];
    /** Full date list including the existing session's date as the first entry. */
    dates: string[];
}

async function promoteToSeries(
    id: string,
    payload: PromoteToSeriesPayload,
): Promise<SeriesCreateResponse> {
    const res = await api.post<SeriesCreateResponse>(
        `/sessions/${id}/promote-to-series`,
        payload,
    );
    return res.data;
}

/*
 * Mutation hooks invalidate the full `['sessions', ...]` query family
 * because a create / update / delete can affect any date window the UI
 * has cached (e.g., dragging a session from this week into next week).
 * TanStack Query matches the prefix, so every cached window refetches.
 */

export function useCreateSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: createSession,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
        },
    });
}

export function useUpdateSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: SessionUpdatePayload }) =>
            updateSession(id, payload),
        // Optimistic update: patch the session in every cached date window so
        // a drag / resize / status change renders at the new position on the
        // same frame the transform clears. The cache write must happen
        // synchronously (before any await) — otherwise the drag tile
        // bounces back to its old slot for a frame while the microtask
        // after cancelQueries runs.
        onMutate: ({ id, payload }) => {
            const snapshots = qc.getQueriesData<SessionRecord[]>({ queryKey: ['sessions'] });
            qc.setQueriesData<SessionRecord[]>(
                { queryKey: ['sessions'] },
                (prev) => {
                    if (!prev) return prev;
                    return prev.map((s) => (s.id === id ? { ...s, ...payload } : s));
                },
            );
            // Fire-and-forget cancel: if an in-flight fetch lands with stale
            // data, onSettled's invalidate will reconcile on completion.
            qc.cancelQueries({ queryKey: ['sessions'] });
            return { snapshots };
        },
        onError: (_err, _vars, context) => {
            if (!context?.snapshots) return;
            for (const [key, data] of context.snapshots) {
                qc.setQueryData(key, data);
            }
        },
        onSettled: (_data, _err, vars) => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
            qc.invalidateQueries({ queryKey: ['session-detail', vars.id] });
            qc.invalidateQueries({ queryKey: ['session-history', vars.id] });
        },
    });
}

export function useDeleteSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: deleteSession,
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
            qc.removeQueries({ queryKey: ['session-detail', id] });
        },
    });
}

export function useCompleteSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: completeSession,
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
            qc.invalidateQueries({ queryKey: ['session-detail', id] });
            qc.invalidateQueries({ queryKey: ['session-history', id] });
        },
    });
}

export function useCreateSeries() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: createSeries,
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
        },
    });
}

export function useUpdateSeries() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ seriesId, fromDate, payload }: {
            seriesId: string;
            fromDate: string;
            payload: SeriesUpdatePayload;
        }) => updateSeries(seriesId, fromDate, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
            // A bulk-series edit can touch any cached detail. Don't know
            // which ids up front, so invalidate the whole family.
            qc.invalidateQueries({ queryKey: ['session-detail'] });
            qc.invalidateQueries({ queryKey: ['session-history'] });
        },
    });
}

export function useDeleteSeries() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ seriesId, fromDate }: { seriesId: string; fromDate: string }) =>
            deleteSeries(seriesId, fromDate),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
            qc.invalidateQueries({ queryKey: ['session-detail'] });
            qc.invalidateQueries({ queryKey: ['session-history'] });
        },
    });
}

export function useUpdateSeriesEndDate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ seriesId, endDate }: { seriesId: string; endDate: string }) =>
            updateSeriesEndDate(seriesId, endDate),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
            qc.invalidateQueries({ queryKey: ['session-detail'] });
            qc.invalidateQueries({ queryKey: ['session-history'] });
        },
    });
}

export function usePromoteToSeries() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: PromoteToSeriesPayload }) =>
            promoteToSeries(id, payload),
        onSuccess: (_data, vars) => {
            qc.invalidateQueries({ queryKey: ['sessions'] });
            qc.invalidateQueries({ queryKey: ['session-detail', vars.id] });
            qc.invalidateQueries({ queryKey: ['session-history', vars.id] });
        },
    });
}

/**
 * One entry in a session's per-row change log. Stored in the
 * `sessions.change_log` JSONB column and returned as-is from
 * GET /api/sessions/:id/history. Tracks Detail-tab changes only;
 * note-data edits and EVV check-in/out are intentionally not recorded.
 *
 * `field` is the canonical key; some collapse the FK + denormalized name
 * into one entry (`staff`, `client`). FK fields whose label needs a
 * directory lookup (`session_type_id`, `supervising_bcba_id`,
 * `cancel_reason_id`) carry the raw UUID; the UI resolves it.
 */
export type SessionHistoryAction =
    | 'created'
    | 'updated'
    | 'completed'
    | 'cancelled'
    | 'promoted_to_series'
    | 'series_updated';

export interface SessionHistoryChange {
    field: string;
    from: string | number | boolean | null;
    to: string | number | boolean | null;
}

export interface SessionHistoryEntry {
    at: string;
    by: { userId: string | null; userName: string | null };
    action: SessionHistoryAction;
    changes: SessionHistoryChange[];
}

async function fetchSessionHistory(id: string): Promise<SessionHistoryEntry[]> {
    const res = await api.get<{ entries: SessionHistoryEntry[] }>(`/sessions/${id}/history`);
    return res.data.entries;
}

export function useSessionHistory(id: string | undefined) {
    return useQuery({
        queryKey: ['session-history', id] as const,
        queryFn: () => fetchSessionHistory(id!),
        enabled: !!id,
    });
}
